/**
 * Cemento Tile Form
 * Photo-only colour swatches (built from the product's Colour option), a
 * square-footage calculator, and add-to-cart for both full boxes and samples.
 *
 * Works on a single product whose options are e.g. Colour + Type (Box / Sample).
 * Integrates with the Concept theme cart drawer (cart:refresh) and switches the
 * gallery on colour change the way the native variant picker does.
 */
(function () {
  if (customElements.get('cemento-tile-form')) return;

  var ADD_URL = (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js';
  var CART_URL = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';

  class CementoTileForm extends HTMLElement {
    constructor() {
      super();
      this.variants = [];
      this.colors = [];          // [{ color, box, sample, image }]
      this.selected = null;
    }

    connectedCallback() {
      try {
        this.variants = JSON.parse(this.querySelector('.cemento-variant-data').textContent.trim());
      } catch (e) {
        this.variants = [];
      }

      this.symbol = this.dataset.currencySymbol || '$';
      this.sectionId = this.dataset.sectionId;
      this.productId = this.dataset.productId;
      this.productUrl = this.dataset.productUrl || '';

      this.els = {
        colorsGrid: this.querySelector('[data-cemento-colors] .cemento-colors__grid'),
        sampleWrap: this.querySelector('[data-cemento-sample-wrap]'),
        samplePrice: this.querySelector('[data-cemento-sample-price]'),
        sampleButton: this.querySelector('[data-cemento-sample-button]'),
        sampleWrap1: this.querySelector('[data-cemento-sample-wrap-1]'),
        samplePrice1: this.querySelector('[data-cemento-sample-price-1]'),
        sampleButton1: this.querySelector('[data-cemento-sample-button-1]'),
        sampleWrap2: this.querySelector('[data-cemento-sample-wrap-2]'),
        samplePrice2: this.querySelector('[data-cemento-sample-price-2]'),
        sampleButton2: this.querySelector('[data-cemento-sample-button-2]'),
        pricePerSqft: this.querySelector('[data-cemento-price-per-sqft]'),
        sqftPerBox: this.querySelector('[data-cemento-sqft-per-box]'),
        pieces: this.querySelector('[data-cemento-pieces]'),
        boxPrice: this.querySelector('[data-cemento-box-price]'),
        needed: this.querySelector('[data-cemento-needed]'),
        overage: this.querySelector('[data-cemento-overage]'),
        overageDesc: this.querySelector('[data-cemento-overage-desc]'),
        overageWrap: this.querySelector('[data-cemento-overage-wrap]'),
        overageBtn: this.querySelector('[data-cemento-overage-btn]'),
        overageLabel: this.querySelector('[data-cemento-overage-label]'),
        overageMenu: this.querySelector('[data-cemento-overage-menu]'),
        totalBoxes: this.querySelector('[data-cemento-total-boxes]'),
        totalSqft: this.querySelector('[data-cemento-total-sqft]'),
        totalPrice: this.querySelector('[data-cemento-total-price]'),
        addButton: this.querySelector('[data-cemento-add-button]'),
        error: this.querySelector('[data-cemento-error]')
      };

      this.hideSwatches = this.dataset.hideSwatches === 'true';
      this.dual = this.dataset.dualSamples === 'true';
      this.kw1 = (this.dataset.sample1Keyword || '').toLowerCase();
      this.kw2 = (this.dataset.sample2Keyword || '').toLowerCase();

      if (this.hideSwatches) {
        // Separate-product-per-colour mode: colours come from the sibling picker.
        this.buyable = this.variants.filter(function (v) { return !v.is_sample; });
        this.samples = this.variants.filter(function (v) { return v.is_sample; });
        if (!this.buyable.length && this.variants.length) this.buyable = this.variants.slice();
      } else {
        this.buildColors();
        this.renderSwatches();
      }

      var def = this.dataset.defaultOverage;
      if (def && this.els.overage) {
        var opt = this.els.overage.querySelector('option[value="' + def + '"]');
        if (opt) this.els.overage.value = def;
      }

      if (this.els.needed) this.els.needed.addEventListener('input', this.recalc.bind(this));
      if (this.els.overage) {
        this.els.overage.addEventListener('change', this.recalc.bind(this));
        this.els.overage.addEventListener('change', this.updateOverageDesc.bind(this));
      }
      this.updateOverageDesc();
      this.initOverageDropdown();
      if (this.els.addButton) this.els.addButton.addEventListener('click', this.addBoxes.bind(this));
      if (this.els.sampleButton) this.els.sampleButton.addEventListener('click', this.addSample.bind(this));
      if (this.els.sampleButton1) this.els.sampleButton1.addEventListener('click', this.addSample1.bind(this));
      if (this.els.sampleButton2) this.els.sampleButton2.addEventListener('click', this.addSample2.bind(this));

      if (this.hideSwatches) {
        var b = this.buyable.find(function (v) { return v.available; }) || this.buyable[0];
        if (b) this.selectSingle(b);
      } else {
        var first = this.colors.find(function (c) { return c.box && c.box.available; }) || this.colors[0];
        if (first) this.selectColor(first, true);
      }
    }

    /* ---------- single product (colours are separate products) ---------- */

    selectSingle(v) {
      this.selected = { box: v, sample: this.samples[0] || null };
      var sqft = parseFloat(v.sqft_per_box) || 0;
      var price = (v.price || 0) / 100;
      this.els.sqftPerBox.textContent = sqft ? this.trim(sqft) : '-';
      this.els.boxPrice.textContent = this.money(price);
      this.els.pricePerSqft.textContent = sqft ? this.money(price / sqft) : '-';
      if (this.els.pieces) {
        var pcs = v.pieces_per_box;
        this.els.pieces.textContent = (pcs === '' || pcs == null) ? '-' : pcs;
      }
      this.applySamples(this.samples);
      this.recalc();
    }

    /* ---------- samples (single or dual: e.g. Sheet + Swatch) ---------- */

    applySamples(samples) {
      samples = samples || [];
      if (this.dual) {
        var k1 = this.kw1, k2 = this.kw2;
        var s1 = k1 ? samples.find(function (s) { return ((s.title || '') + '').toLowerCase().indexOf(k1) !== -1; }) : samples[0];
        var s2 = k2 ? samples.find(function (s) { return ((s.title || '') + '').toLowerCase().indexOf(k2) !== -1; }) : samples[1];
        this._sample1 = s1 || null;
        this._sample2 = s2 || null;
        this.fillSampleBox(this.els.sampleWrap1, this.els.samplePrice1, this.els.sampleButton1, s1);
        this.fillSampleBox(this.els.sampleWrap2, this.els.samplePrice2, this.els.sampleButton2, s2);
      } else {
        var s = samples[0] || null;
        this._sample = s;
        this.fillSampleBox(this.els.sampleWrap, this.els.samplePrice, this.els.sampleButton, s);
      }
    }

    fillSampleBox(wrap, priceEl, btn, sample) {
      if (!wrap) return;
      if (sample) {
        wrap.hidden = false;
        if (priceEl) priceEl.textContent = this.money((sample.price || 0) / 100);
        if (btn) btn.disabled = !sample.available;
      } else {
        wrap.hidden = true;
      }
    }

    buildColors() {
      var map = {};
      this.variants.forEach(function (v) {
        var key = (v.color || v.title || '').toString();
        if (!map[key]) map[key] = { color: key, box: null, sample: null, samples: [], image: null };
        if (v.is_sample) {
          if (!map[key].sample) map[key].sample = v;
          map[key].samples.push(v);
        } else {
          if (!map[key].box || (!map[key].box.available && v.available)) map[key].box = v;
          if (!map[key].image && v.image) map[key].image = v.image;
        }
        if (!map[key].image && v.image) map[key].image = v.image;
      });
      this.colors = Object.keys(map).map(function (k) { return map[k]; })
        .filter(function (c) { return c.box; });
    }

    renderSwatches() {
      if (!this.els.colorsGrid) return;
      this.els.colorsGrid.innerHTML = '';
      var self = this;
      this.colors.forEach(function (c) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cemento-swatch';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', 'false');
        btn.setAttribute('aria-label', c.color);
        btn.title = c.color;
        if (!c.box.available) btn.classList.add('is-soldout');
        if (c.image) {
          var img = document.createElement('img');
          img.src = c.image; img.alt = c.color; img.loading = 'lazy';
          btn.appendChild(img);
        } else {
          btn.textContent = c.color.slice(0, 2);
        }
        btn.addEventListener('click', function () { self.selectColor(c); });
        c._el = btn;
        self.els.colorsGrid.appendChild(btn);
      });
    }

    selectColor(c, skipGallery) {
      this.selected = c;
      this.colors.forEach(function (other) {
        if (other._el) {
          var on = other === c;
          other._el.classList.toggle('is-selected', on);
          other._el.setAttribute('aria-checked', on ? 'true' : 'false');
        }
      });

      var box = c.box;
      var sqft = parseFloat(box.sqft_per_box) || 0;
      var price = (box.price || 0) / 100;
      this.els.sqftPerBox.textContent = sqft ? this.trim(sqft) : '-';
      this.els.boxPrice.textContent = this.money(price);
      this.els.pricePerSqft.textContent = sqft ? this.money(price / sqft) : '-';
      if (this.els.pieces) {
        var pcs = box.pieces_per_box;
        this.els.pieces.textContent = (pcs === '' || pcs == null) ? '-' : pcs;
      }

      this.applySamples(c.samples && c.samples.length ? c.samples : (c.sample ? [c.sample] : []));

      this.recalc();

      if (!skipGallery && String(box.id) !== this._lastGalleryVariant) {
        this.updateGallery(box.id);
      }
      this._lastGalleryVariant = String(box.id);
    }

    updateGallery(variantId) {
      if (!this.productUrl || !this.sectionId) return;
      var self = this;
      var sep = this.productUrl.indexOf('?') === -1 ? '?' : '&';
      var url = this.productUrl + sep + 'variant=' + encodeURIComponent(variantId) +
        '&section_id=' + encodeURIComponent(this.sectionId);
      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          ['ProductGallery', 'Price', 'BuyButtonPrice', 'Sku', 'Badges'].forEach(function (base) {
            var id = base + '-' + self.sectionId + '-' + self.productId;
            var src = doc.getElementById(id);
            var dst = document.getElementById(id);
            if (src && dst) dst.innerHTML = src.innerHTML;
          });
          try {
            var u = new URL(window.location.href);
            u.searchParams.set('variant', variantId);
            window.history.replaceState({ path: u.toString() }, '', u.toString());
          } catch (e) {}
        })
        .catch(function () {});
    }

    initOverageDropdown() {
      var self = this;
      if (!this.els.overage || !this.els.overageMenu || !this.els.overageBtn) return;
      var opts = Array.prototype.slice.call(this.els.overage.options);
      this.els.overageMenu.innerHTML = '';
      opts.forEach(function (o) {
        var li = document.createElement('li');
        li.className = 'cemento-overage__opt';
        li.setAttribute('role', 'option');
        li.dataset.value = o.value;
        var label = document.createElement('span');
        label.className = 'cemento-overage__opt-label';
        label.textContent = o.textContent;
        var check = document.createElement('span');
        check.className = 'cemento-overage__check';
        check.setAttribute('aria-hidden', 'true');
        label.appendChild(check);
        li.appendChild(label);
        var desc = o.getAttribute('data-desc') || '';
        if (desc) {
          var d = document.createElement('span');
          d.className = 'cemento-overage__opt-desc';
          d.textContent = desc;
          li.appendChild(d);
        }
        li.addEventListener('click', function () { self.selectOverage(o.value); });
        self.els.overageMenu.appendChild(li);
      });

      this.els.overageBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        self.toggleOverageMenu();
      });
      document.addEventListener('click', function (e) {
        if (self.els.overageWrap && !self.els.overageWrap.contains(e.target)) self.closeOverageMenu();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.closeOverageMenu();
      });

      this.syncOverageUI();
    }

    toggleOverageMenu() {
      if (this.els.overageMenu.hidden) this.openOverageMenu(); else this.closeOverageMenu();
    }
    openOverageMenu() {
      this.els.overageMenu.hidden = false;
      this.els.overageBtn.setAttribute('aria-expanded', 'true');
      this.els.overageWrap.classList.add('is-open');
    }
    closeOverageMenu() {
      if (!this.els.overageMenu) return;
      this.els.overageMenu.hidden = true;
      this.els.overageBtn.setAttribute('aria-expanded', 'false');
      this.els.overageWrap.classList.remove('is-open');
    }
    selectOverage(value) {
      this.els.overage.value = value;
      this.els.overage.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncOverageUI();
      this.closeOverageMenu();
    }
    syncOverageUI() {
      var opt = this.els.overage.options[this.els.overage.selectedIndex];
      if (this.els.overageLabel && opt) this.els.overageLabel.textContent = opt.textContent;
      var val = this.els.overage.value;
      Array.prototype.forEach.call(this.els.overageMenu.querySelectorAll('.cemento-overage__opt'), function (li) {
        var on = li.dataset.value === val;
        li.classList.toggle('is-selected', on);
        li.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    updateOverageDesc() {
      var el = this.els.overageDesc;
      if (!el || !this.els.overage) return;
      var opt = this.els.overage.options[this.els.overage.selectedIndex];
      var desc = opt ? (opt.getAttribute('data-desc') || '') : '';
      el.textContent = desc;
      el.hidden = desc === '';
    }

    recalc() {
      if (!this.selected) return;
      var box = this.selected.box;
      var sqftPerBox = parseFloat(box.sqft_per_box) || 0;
      var price = (box.price || 0) / 100;
      var needed = parseFloat(this.els.needed.value) || 0;
      var overage = (parseFloat(this.els.overage.value) || 0) / 100;

      if (needed <= 0 || sqftPerBox <= 0) {
        this.els.totalBoxes.textContent = '-';
        this.els.totalSqft.textContent = '-';
        this.els.totalPrice.textContent = '-';
        this._boxes = 0;
        return;
      }
      var boxes = Math.ceil(needed * (1 + overage) / sqftPerBox);
      if (boxes < 1) boxes = 1;
      this._boxes = boxes;
      this.els.totalBoxes.textContent = boxes;
      this.els.totalSqft.textContent = this.trim(boxes * sqftPerBox);
      this.els.totalPrice.textContent = this.money(boxes * price);
    }

    addBoxes() {
      if (!this.selected) return;
      var boxes = this._boxes || 0;
      if (boxes < 1) {
        this.showError('Please enter the square footage you need.');
        if (this.els.needed) this.els.needed.focus();
        return;
      }
      this.addToCart(this.selected.box.id, boxes, this.els.addButton);
    }

    addSample() {
      if (!this._sample) return;
      this.addToCart(this._sample.id, 1, this.els.sampleButton);
    }
    addSample1() {
      if (!this._sample1) return;
      this.addToCart(this._sample1.id, 1, this.els.sampleButton1);
    }
    addSample2() {
      if (!this._sample2) return;
      this.addToCart(this._sample2.id, 1, this.els.sampleButton2);
    }

    addToCart(id, quantity, button) {
      var self = this;
      this.clearError();
      if (button) { button.classList.add('is-loading'); button.disabled = true; }

      var body = new FormData();
      body.append('id', id);
      body.append('quantity', quantity);

      fetch(ADD_URL, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: body
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.status) {
            self.showError(data.description || data.message || 'Could not add to cart.');
            return;
          }
          var refreshed = false;
          try {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
            refreshed = !!document.querySelector('cart-drawer');
          } catch (e) {}
          if (!refreshed) window.location.href = CART_URL;
        })
        .catch(function () { self.showError('Something went wrong. Please try again.'); })
        .finally(function () {
          if (button) { button.classList.remove('is-loading'); button.disabled = false; }
        });
    }

    money(value) {
      var n = Number(value) || 0;
      return this.symbol + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    trim(n) { return (Math.round(n * 100) / 100).toString(); }
    showError(msg) { if (this.els.error) { this.els.error.textContent = msg; this.els.error.hidden = false; } }
    clearError() { if (this.els.error) { this.els.error.hidden = true; this.els.error.textContent = ''; } }
  }

  customElements.define('cemento-tile-form', CementoTileForm);
})();

/* ---- Colour swatches: show "View All Colors N" + popup when more than 8 ---- */
(function () {
  var MAX = 8;
  var modal;
  function ready(fn) { document.readyState !== 'loading' ? fn() : document.addEventListener('DOMContentLoaded', fn); }

  function buildModal() {
    modal = document.createElement('div');
    modal.className = 'cemento-colormodal';
    modal.hidden = true;
    modal.innerHTML =
      '<div class="cemento-colormodal__overlay" data-close></div>' +
      '<div class="cemento-colormodal__panel" role="dialog" aria-modal="true" aria-label="All colors">' +
        '<div class="cemento-colormodal__head">' +
          '<span class="cemento-colormodal__title"></span>' +
          '<button type="button" class="cemento-colormodal__close" data-close aria-label="Close">&times;</button>' +
        '</div>' +
        '<div class="cemento-colormodal__grid"></div>' +
      '</div>';
    modal.addEventListener('click', function (e) { if (e.target.hasAttribute('data-close')) closeModal(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeModal(); });
    document.body.appendChild(modal);
    return modal;
  }
  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(function () { if (modal && !modal.classList.contains('is-open')) modal.hidden = true; }, 320);
  }
  function openModal(buildGrid, count) {
    modal = modal || buildModal();
    var grid = modal.querySelector('.cemento-colormodal__grid');
    grid.innerHTML = '';
    buildGrid(grid);
    modal.querySelector('.cemento-colormodal__title').textContent = 'All ' + count + ' colors';
    modal.hidden = false;
    void modal.offsetWidth; /* reflow so the slide-in transition runs */
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }
  function upscale(root) {
    /* load larger images for the big drawer view */
    Array.prototype.forEach.call(root.querySelectorAll('.color-swatch'), function (a) {
      var st = a.getAttribute('style') || '';
      if (st) a.setAttribute('style', st.replace(/width=\d+/g, 'width=640'));
    });
    Array.prototype.forEach.call(root.querySelectorAll('img'), function (img) {
      if (img.src) img.src = img.src.replace(/width=\d+/g, 'width=640');
      img.removeAttribute('srcset');
    });
  }
  function addLink(afterEl, count, buildGrid) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cemento-viewall-colors';
    btn.textContent = 'View All Colors ' + count;
    afterEl.parentNode.insertBefore(btn, afterEl.nextSibling);
    btn.addEventListener('click', function () { openModal(buildGrid, count); });
  }

  function enhance() {
    /* Native theme colour swatches (sibling products or colour variants) */
    Array.prototype.forEach.call(document.querySelectorAll('ul.swatches'), function (ul) {
      if (ul.dataset.cementoViewall) return;
      var items = Array.prototype.filter.call(ul.children, function (li) {
        return li.querySelector && li.querySelector('.color-swatch');
      });
      if (items.length <= MAX) return;
      ul.dataset.cementoViewall = '1';
      ul.classList.add('cemento-swatches--limited');
      addLink(ul, items.length, function (grid) {
        var clone = ul.cloneNode(true);
        clone.classList.remove('cemento-swatches--limited');
        upscale(clone);
        grid.appendChild(clone);
      });
    });

    /* Calculator's own colour swatches (variant-based) */
    Array.prototype.forEach.call(document.querySelectorAll('.cemento-colors__grid'), function (g) {
      if (g.dataset.cementoViewall) return;
      var btns = Array.prototype.filter.call(g.children, function (b) {
        return b.classList && b.classList.contains('cemento-swatch');
      });
      if (btns.length <= MAX) return;
      g.dataset.cementoViewall = '1';
      g.classList.add('cemento-swatches--limited');
      addLink(g, btns.length, function (grid) {
        var wrap = document.createElement('div');
        wrap.className = 'cemento-colors__grid';
        btns.forEach(function (orig) {
          var c = orig.cloneNode(true);
          c.addEventListener('click', function () { orig.click(); closeModal(); });
          wrap.appendChild(c);
        });
        upscale(wrap);
        grid.appendChild(wrap);
      });
    });
  }
  ready(enhance);
})();
